import { Component, Input, OnChanges, EventEmitter, Output, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RenderFilter, FilterSelectionAction } from 'gung-list';
import { environment } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import {
  HlDisplayPortalFeaturesService,
  ProductListInfoSettingsGroup
} from '../../services/hl-display-portal-features.service';
import { filter, first, forkJoin, Observable, of, switchMap } from 'rxjs';
import {
  AssortmentService,
  CartService,
  GungFlowService,
  GungModalService,
  Pricelist,
  PriceService,
  Product,
  ProductService
} from 'gung-standard';
import { OptionsList } from 'gung-common';
import { HlDisplayTemplatePortalService } from '../../services/hl-display-template-portal.service';

interface CategoryTreeNode {
  id: string;
  name: string;
  selected: boolean;
  expanded: boolean;
  itemsIncluded: string[];
  subcategories: CategoryTreeNode[];
  filterName: string; // 'CATEGORY' or 'EXTERNAL_SUB_CATEGORY'
}

const removeRenderFilterWithZeroItemIncludedInSelectedFilterCategoryItemsIncluded = (
  selectedCategoryValue: any,
  regularFilters: RenderFilter[]
): RenderFilter[] => {
  // included items in selected category
  const itemsIncludedSelectedCategory = selectedCategoryValue.itemsIncluded;

  // create a copy of regular filters
  const regularFiltersCopy = JSON.parse(JSON.stringify(regularFilters)) as RenderFilter[];

  // go through all regular filters and get the items inclued
  regularFiltersCopy.forEach(regularFilter => {
    // get all items included in this filter
    const itemsIncludedRenderFilter = Array.from(
      new Set<string>(
        regularFilter.valueList
          .filter(v => !!v.valueId)
          .map(v => v.itemsIncluded)
          .reduce((a, b) => a.concat(b), [])
      )
    );

    // if none of items included in the selected category
    // exists in the items included in the filter
    // then remove the filter from the regular filters array
    if (itemsIncludedSelectedCategory.filter(x => itemsIncludedRenderFilter.includes(x)).length === 0) {
      const indexToRemove = regularFilters.findIndex(r => r.name === regularFilter.name);
      if (indexToRemove > -1) {
        regularFilters.splice(indexToRemove, 1);
      }
    }
  });

  return regularFilters;
};

const getRegularFilters = (allFilters: RenderFilter[], categoryFilter: RenderFilter): RenderFilter[] => {
  const categorySelected = categoryFilter.valueList.filter(value => value.selected)[0];

  const regularFilters = allFilters.filter(
    f =>
      f.name !== 'MOUSQUETAIRES_SELECTION' &&
      f.name !== 'CATEGORY' &&
      f.name !== 'CUSTOMER_PURCHASED_PRODUCT' &&
      f.name !== 'DISCOUNTED_ITEMS'
  );
  if (!categorySelected) {
    return regularFilters;
  }

  return removeRenderFilterWithZeroItemIncludedInSelectedFilterCategoryItemsIncluded(categorySelected, regularFilters);
};

@Component({
  selector: 'app-hl-product-list-filters',
  templateUrl: './hl-product-list-filters.component.html',
  styleUrls: ['./hl-product-list-filters.component.scss'],
  standalone: false
})
export class HlProductListFiltersComponent implements OnInit, OnChanges {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  public translateService = inject(TranslateService);
  protected hldisplayPortalFeaturesService = inject(HlDisplayPortalFeaturesService);
  protected hldisplayTemplatePortalService = inject(HlDisplayTemplatePortalService);
  protected assortmentService = inject(AssortmentService);
  protected gungFlowService = inject(GungFlowService);
  protected productService = inject(ProductService);
  protected cartService = inject(CartService);
  protected gungModalService = inject(GungModalService);

  public showMore = false;
  public showMoreLimit = 5;
  @Input()
  renderFilters: RenderFilter[];

  @Output()
  selection: EventEmitter<FilterSelectionAction[]> = new EventEmitter();

  public loaded = false;

  public categoryFilter: RenderFilter;
  public categoryTree: CategoryTreeNode[] = [];
  public hasAnySubcategories = false;

  public specialSelectionFilters: RenderFilter[];

  public regularFilters: RenderFilter[];

  protected assortmentOptions: OptionsList[] = [];
  protected selectedAssortmentId: string;

  ngOnInit(): void {
    // as for customer portal the category filter can have duplicated names, so they are merge
    // so the queryParameter CATEGORY contains several filter ; separated
    // but for avoind issues from the categories page allways is passed one filterId
    // so in order to get a correct behaviour we have try to find the corrected filterValueId
    if (!!this.route.snapshot.queryParams) {
      const from: string = this.route.snapshot.queryParams.from || '';
      const filters: string = this.route.snapshot.queryParams.filters || '';

      if (!!from && !!filters && from === 'categories' && filters.indexOf('CATEGORY') > -1) {
        // get selected category filter
        const categoryFilter = this.checkSelectedCategoryFilter(
          this.renderFilters.filter(f => f.name === 'CATEGORY')[0]
        );
        if (!!categoryFilter) {
          const categoryFilterSelected = categoryFilter.valueList.filter(vl => vl.selected)[0];
          if (!!categoryFilterSelected) {
            if (filters.indexOf(categoryFilterSelected.valueId) < 0) {
              this.router.navigate(['/products'], {
                queryParams: { filters: 'CATEGORY__:__' + categoryFilterSelected.valueId },
                queryParamsHandling: 'merge'
              });
            }
          }
        }
      }
    }

    this.hldisplayPortalFeaturesService
      .getPortalFeatureGroup('productListInfoSettingsGroup')
      .pipe(first())
      .subscribe((features: ProductListInfoSettingsGroup) => {
        if (!!features.templatePortal) {
          this.loadTemplatePortalFeatures();
        }
      });
  }

  ngOnChanges() {
    // DO NOT REMOVE OR SPECIAL SELECTION AND CATEGORY FILTERS STOP WORKING!!!!
    this.specialSelectionFilters = this.renderFilters.filter(
      f =>
        f.name === 'MOUSQUETAIRES_SELECTION' || f.name === 'CUSTOMER_PURCHASED_PRODUCT' || f.name === 'DISCOUNTED_ITEMS'
    );
    this.categoryFilter = this.renderFilters.filter(f => f.name === 'CATEGORY')[0];
    this.regularFilters = this.getRegularFilters(this.renderFilters, this.categoryFilter);

    // Build category tree
    this.buildCategoryTree();

    // DO NOT REMOVE THIS LINE OF CODE OR NOTHING WORKS!!!!
    this.loaded = true;
  }

  protected getRegularFilters(allFilters: RenderFilter[], categoryFilter: RenderFilter): RenderFilter[] {
    const categorySelected = categoryFilter?.valueList.filter(value => value.selected)[0];

    const regularFilters = allFilters.filter(
      f =>
        f.name !== 'MOUSQUETAIRES_SELECTION' &&
        f.name !== 'CATEGORY' &&
        f.name !== 'CUSTOMER_PURCHASED_PRODUCT' &&
        f.name !== 'DISCOUNTED_ITEMS'
    );
    return this.removeRenderFilterWithZeroItemIncludedInSelectedFilterCategoryItemsIncluded(
      categorySelected,
      regularFilters
    );
  }

  protected removeRenderFilterWithZeroItemIncludedInSelectedFilterCategoryItemsIncluded(
    selectedCategoryValue: any,
    regularFilters: RenderFilter[]
  ): RenderFilter[] {
    // included items in selected category

    if (!selectedCategoryValue) {
      return regularFilters;
    }
    const itemsIncludedSelectedCategory = selectedCategoryValue.itemsIncluded;

    // create a copy of regular filters
    const regularFiltersCopy = JSON.parse(JSON.stringify(regularFilters)) as RenderFilter[];

    // go through all regular filters and get the items inclued
    regularFiltersCopy.forEach(regularFilter => {
      // get all items included in this filter
      const itemsIncludedRenderFilter = Array.from(
        new Set<string>(
          regularFilter.valueList
            .filter(v => !!v.valueId)
            .map(v => v.itemsIncluded)
            .reduce((a, b) => a.concat(b), [])
        )
      );

      // if none of items included in the selected category
      // exists in the items included in the filter
      // then remove the filter from the regular filters array
      if (itemsIncludedSelectedCategory.filter(x => itemsIncludedRenderFilter.includes(x)).length === 0) {
        const indexToRemove = regularFilters.findIndex(r => r.name === regularFilter.name);
        if (indexToRemove > -1) {
          regularFilters.splice(indexToRemove, 1);
        }
      }
    });

    return regularFilters;
  }

  public specialSelectionClicked(selectionFilter: RenderFilter) {
    const specialSelectionFilter = this.specialSelectionFilters.find(f => f.name === selectionFilter.name);
    if (specialSelectionFilter) {
      this.selection.emit([
        {
          filterName: specialSelectionFilter.name,
          optionId: specialSelectionFilter.valueList[0].valueId,
          selected: !specialSelectionFilter.valueList[0].selected
        }
      ]);
    }

    return false;
  }

  public setSelection(filterName: string, optionId: string | null, selected: boolean, optionIdRange?: string[]) {
    this.selection.emit([
      {
        filterName,
        optionId,
        selected,
        optionIdRange
      }
    ]);
  }

  protected buildCategoryTree(): void {
    if (!this.categoryFilter || !this.renderFilters) {
      this.categoryTree = [];
      return;
    }

    const subCategoryFilter = this.renderFilters.find(f => f.name === 'EXTERNAL_SUB_CATEGORY');

    // Build map of subcategories by their parent category
    // We'll match subcategories to categories based on shared itemsIncluded
    const subcategoryMap = new Map<string, CategoryTreeNode[]>();

    if (subCategoryFilter) {
      subCategoryFilter.valueList.forEach(subCatValue => {
        if (!subCatValue.valueId) return;

        const subCatNode: CategoryTreeNode = {
          id: subCatValue.valueId,
          name: subCatValue.valueName,
          selected: subCatValue.selected,
          expanded: false,
          itemsIncluded: subCatValue.itemsIncluded || [],
          subcategories: [],
          filterName: 'EXTERNAL_SUB_CATEGORY'
        };

        // Find parent categories that share items with this subcategory
        this.categoryFilter.valueList.forEach(catValue => {
          if (!catValue.valueId) return;

          const sharedItems = (catValue.itemsIncluded || []).filter(item =>
            (subCatValue.itemsIncluded || []).includes(item)
          );

          if (sharedItems.length > 0) {
            if (!subcategoryMap.has(catValue.valueId)) {
              subcategoryMap.set(catValue.valueId, []);
            }
            subcategoryMap.get(catValue.valueId)!.push(subCatNode);
          }
        });
      });
    }

    // Build tree with categories as top level
    this.categoryTree = this.categoryFilter.valueList
      .filter(catValue => !!catValue.valueId)
      .map(catValue => {
        const subcategories = subcategoryMap.get(catValue.valueId) || [];

        // Auto-expand category if any of its subcategories are selected
        const hasSelectedSubcategory = subcategories.some(sub => sub.selected);

        return {
          id: catValue.valueId,
          name: catValue.valueName,
          selected: catValue.selected,
          expanded: hasSelectedSubcategory,
          itemsIncluded: catValue.itemsIncluded || [],
          subcategories: subcategories,
          filterName: 'CATEGORY'
        };
      });
    
    // Check if any category has subcategories
    this.hasAnySubcategories = this.categoryTree.some(cat => cat.subcategories.length > 0);
  }

  protected toggleExpand(category: CategoryTreeNode): void {
    category.expanded = !category.expanded;
  }

  protected nodeClicked(node: CategoryTreeNode, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    const actions: FilterSelectionAction[] = [];

    // Deselect all categories and subcategories
    const toDeselect: FilterSelectionAction[] = [];

    // Find parent category if this is a subcategory
    let parentCategory: CategoryTreeNode | null = null;
    if (node.filterName === 'EXTERNAL_SUB_CATEGORY') {
      for (const cat of this.categoryTree) {
        if (cat.subcategories.some(sub => sub.id === node.id)) {
          parentCategory = cat;
          break;
        }
      }
    }

    this.categoryTree.forEach(cat => {
      // Don't deselect the parent category if we're selecting a subcategory
      if (cat.selected && cat.id !== node.id && cat !== parentCategory) {
        toDeselect.push({ filterName: cat.filterName, optionId: cat.id, selected: false });
      }
      cat.subcategories.forEach(subCat => {
        if (subCat.selected && subCat.id !== node.id) {
          toDeselect.push({ filterName: subCat.filterName, optionId: subCat.id, selected: false });
        }
      });
    });

    // Deselect all other regular filters
    const regularFiltersDeselectActions = this.regularFilters
      .filter(f => f.name !== 'ASSORTMENT_TYPE')
      .map(f =>
        f.valueList
          .filter(value => value.selected)
          .map(value => ({
            filterName: f.name,
            optionId: value.valueId,
            selected: false
          }))
      )
      .reduce((acc, curr) => [...curr, ...acc], []);

    toDeselect.push(...regularFiltersDeselectActions);

    // Add all deselect actions
    actions.push(...toDeselect);

    // If selecting a subcategory, also select its parent category and expand it
    if (node.filterName === 'EXTERNAL_SUB_CATEGORY' && parentCategory && !node.selected) {
      actions.push({
        filterName: parentCategory.filterName,
        optionId: parentCategory.id,
        selected: true
      });
      // Expand the parent category to show the selected subcategory
      parentCategory.expanded = true;
    }

    // Toggle current node selection
    actions.push({
      filterName: node.filterName,
      optionId: node.id,
      selected: !node.selected
    });

    this.selection.emit(actions);
  }

  public categoryClicked(valueId: string) {
    const actions: FilterSelectionAction[] = [];
    // deselect all currently selected categories
    const toDeselect = this.categoryFilter.valueList
      .filter(value => value.selected)
      .filter(value => value.valueId !== valueId)
      .map(value => ({ filterName: 'CATEGORY', optionId: value.valueId, selected: false }));
    // deselect all other regular filters
    const regularFiltersDeselectActions = this.regularFilters
      .filter(f => f.name !== 'ASSORTMENT_TYPE')
      .map(f =>
        f.valueList
          .filter(value => value.selected)
          .map(value => ({
            filterName: f.name,
            optionId: value.valueId,
            selected: false
          }))
      )
      .reduce((acc, curr) => [...curr, ...acc], []);

    toDeselect.push(...regularFiltersDeselectActions);
    // select or deselect the current value
    const option = this.categoryFilter.valueList.filter(value => value.valueId === valueId)[0];
    if (!option) {
      throw new Error('Invalid option selected, please contact your administrator');
    }

    // add all the actions and emit
    actions.push(...toDeselect);
    actions.push({
      filterName: 'CATEGORY',
      optionId: option.valueId,
      selected: !option.selected
    });
    this.selection.emit(actions);
  }

  private checkSelectedCategoryFilter(renderFilter: RenderFilter): RenderFilter {
    // in case there is not selected filter THEN check from query parameters if there is any
    // this is a fix when coming from categories pages with several ids e.g. FreshProduceSolutions;DisplayAccessories
    // in this case the default behaviour (in GUNG-LIST) which set the selected filter does not occur
    if (!!renderFilter && renderFilter.valueList.filter(v => v.selected).length === 0) {
      if (!!this.route.snapshot.queryParams && !!this.route.snapshot.queryParams.filters) {
        // e.g.: CATEGORY:FreshProduceSolutions;DisplayAccessories or DisplayAccessories;FreshProduceSolutions
        const queryParamsFilters: string = this.route.snapshot.queryParams.filters;

        // try to find the item to selected by the first category in the query parameters
        const itemToSelect = renderFilter.valueList.filter(
          v => v.valueId.indexOf(queryParamsFilters.replace('CATEGORY__:__', '').split(';')[0]) > -1
        )[0];
        if (!!itemToSelect) {
          itemToSelect.selected = true;
        }
      }
    }

    return renderFilter;
  }

  protected loadTemplatePortalFeatures(): void {
    this.hldisplayTemplatePortalService
      .getAssortmentOptions()
      .pipe(
        first(),
        switchMap(assortmentOptions =>
          forkJoin({
            assortmentOptions: of(assortmentOptions),
            selectedAssortmentId: this.hldisplayTemplatePortalService.getSelectedTemplateAssortmentId().pipe(first()),
            currentCart: this.cartService.getCurrentCart().pipe(first())
          })
        )
      )
      .subscribe(({ assortmentOptions, selectedAssortmentId, currentCart }) => {
        this.selectedAssortmentId = selectedAssortmentId;
        this.assortmentOptions = assortmentOptions;
        if (currentCart.length === 0) {
          this.selectAssortment(selectedAssortmentId || assortmentOptions[0].id, true);
        }
      });
  }

  protected selectAssortment(assortmentId: string, forceSelection: boolean): void {
    if (forceSelection) {
      this.hldisplayTemplatePortalService.setSelectedTemplateAssortmentId(assortmentId);
      this.addTemplateToCart(assortmentId);
      return;
    }
    this.hldisplayTemplatePortalService
      .getSelectedTemplateAssortmentId()
      .pipe(first())
      .subscribe(selectedAssortmentId => {
        if (assortmentId !== selectedAssortmentId) {
          this.gungModalService.openConfirmYesNoModal(undefined, 'CLEAR_CART_CONFIRM', { size: 'sm' }).then(
            result => {
              if (result) {
                this.hldisplayTemplatePortalService.setSelectedTemplateAssortmentId(assortmentId);
                this.addTemplateToCart(assortmentId);
              } else {
                this.selectedAssortmentId = selectedAssortmentId;
              }
            },
            reason => {
              this.selectedAssortmentId = selectedAssortmentId;
            }
          );
        }
      });
  }

  protected addTemplateToCart(assortmentId: string): void {
    this.cartService.clearCart();

    this.getTemplatePortalProducts(assortmentId)
      .pipe(first())
      .subscribe(products => {
        const bulkAdd: {
          productId: string;
          qty: number;
          targetStockId?: string;
          productPartialId?: string;
          extra?: { [s: string]: any };
          position?: number;
          productName?: string;
        }[] = [];

        for (const product of products) {
          if (!!product.extra?.templatelimitlowant) {
            bulkAdd.push({
              productId: product.id,
              qty: product.extra.templatelimitlowant,
              targetStockId: undefined,
              productPartialId: undefined,
              extra: { templateAssortmentId: assortmentId },
              position: undefined,
              productName: undefined
            });
          }
        }

        if (bulkAdd.length > 0) {
          this.cartService.bulkAddToCart(bulkAdd);
        }
      });
  }

  protected getTemplatePortalProducts(selectedTemplateAssortmentId: string): Observable<Product[]> {
    return this.gungFlowService.getSelectedFlow().pipe(
      filter(selectedFlow => !!selectedFlow),
      switchMap(selectedFlow =>
        this.productService.getProductsByAssortmentAndFlow(selectedTemplateAssortmentId, selectedFlow.id).pipe(first())
      ),
      switchMap(products =>
        forkJoin({
          products: of(products),
          productAssorment: this.assortmentService.getAssortment(selectedTemplateAssortmentId).pipe(first())
        })
      ),
      switchMap(({ products, productAssorment }) => {
        for (const product of products) {
          const assortmentChild = productAssorment.children.find(a => a.id === product.id);
          product.extra.templatelimitlowant = assortmentChild.extra?.limitlowant || 1;
        }

        return of(products);
      })
    );
  }
}
